<script setup lang="ts">
import convertAzureIcon from '@/utils/azure-icons'
import { notifyError } from '@/utils/helpers'

interface WeatherValue {
    value: number
    unit: string
}

interface CurrentWeather {
    iconCode: number
    isDayTime: boolean
    phrase: string
    temperature: WeatherValue
    realFeelTemperature: WeatherValue
    relativeHumidity: number
    dewPoint: WeatherValue
    wind: { speed: WeatherValue }
    pressure: WeatherValue
    precipitationSummary: { past24Hours: WeatherValue }
    temperatureSummary?: {
        past24Hours: { minimum: WeatherValue; maximum: WeatherValue }
    }
}

const storeU = useUtilisateurStore()
const { isAuthenticated, isPending } = useKeycloak()

const props = withDefaults(defineProps<{ hero?: boolean }>(), { hero: false })

const siteNom = ref('')
const weather = ref<CurrentWeather | null>(null)
const isLoading = ref(true)

const currentDate = computed(() =>
    new Date().toLocaleDateString('fr-CA', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
    }),
)

const isDay = computed(() => weather.value?.isDayTime ?? true)

const heroIcon = computed(() =>
    convertAzureIcon(weather.value?.iconCode ?? 0, isDay.value),
)

// Atmospheric Apple-Weather style gradient (day = bright sky, night = deep blue)
const heroGradient = computed(() =>
    isDay.value
        ? 'linear-gradient(165deg, #3b7fc4 0%, #5a9bd8 45%, #8fbfe8 100%)'
        : 'linear-gradient(165deg, #14213d 0%, #233357 55%, #34456b 100%)',
)

// Today's high / low if available from the current conditions summary
const hiLo = computed(() => {
    const s = weather.value?.temperatureSummary?.past24Hours
    if (!s) return null
    return {
        hi: Math.round(s.maximum.value),
        lo: Math.round(s.minimum.value),
    }
})

// Expose the gradient so the page can paint the whole background to match
defineExpose({ heroGradient })

interface StatTile {
    icon: string
    label: string
    value: string
}

const stats = computed<StatTile[]>(() => {
    const w = weather.value
    if (!w) return []
    return [
        {
            icon: 'M15 13V5A3 3 0 0 0 9 5V13A5 5 0 1 0 15 13M12 4A1 1 0 0 1 13 5V8H11V5A1 1 0 0 1 12 4Z',
            label: 'Ressentie',
            value: `${Math.round(w.realFeelTemperature.value)} ${w.realFeelTemperature.unit}`,
        },
        {
            icon: 'M12,3.25C12,3.25 6,10 6,14C6,17.32 8.69,20 12,20A6,6 0 0,0 18,14C18,10 12,3.25 12,3.25M14.47,9.97L15.53,11.03L9.53,17.03L8.47,15.97M9.75,10A1.25,1.25 0 0,1 11,11.25A1.25,1.25 0 0,1 9.75,12.5A1.25,1.25 0 0,1 8.5,11.25A1.25,1.25 0 0,1 9.75,10M14.25,14.5A1.25,1.25 0 0,1 15.5,15.75A1.25,1.25 0 0,1 14.25,17A1.25,1.25 0 0,1 13,15.75A1.25,1.25 0 0,1 14.25,14.5Z',
            label: 'Humidité',
            value: `${w.relativeHumidity} %`,
        },
        {
            icon: 'M4,10A1,1 0 0,1 3,9A1,1 0 0,1 4,8H12A2,2 0 0,0 14,6A2,2 0 0,0 12,4C11.45,4 10.95,4.22 10.59,4.59C10.2,5 9.56,5 9.17,4.59C8.78,4.2 8.78,3.56 9.17,3.17C9.9,2.45 10.9,2 12,2A4,4 0 0,1 16,6A4,4 0 0,1 12,10H4M19,12A1,1 0 0,0 20,11A1,1 0 0,0 19,10C18.72,10 18.47,10.11 18.29,10.29C17.9,10.68 17.27,10.68 16.88,10.29C16.5,9.9 16.5,9.27 16.88,8.88C17.42,8.34 18.17,8 19,8A3,3 0 0,1 22,11A3,3 0 0,1 19,14H5A1,1 0 0,1 4,13A1,1 0 0,1 5,12H19M18,18H4A1,1 0 0,1 3,17A1,1 0 0,1 4,16H18A3,3 0 0,1 21,19A3,3 0 0,1 18,22C17.17,22 16.42,21.66 15.88,21.12C15.5,20.73 15.5,20.1 15.88,19.71C16.27,19.32 16.9,19.32 17.29,19.71C17.47,19.89 17.72,20 18,20A1,1 0 0,0 19,19A1,1 0 0,0 18,18Z',
            label: 'Vents',
            value: `${Math.round(w.wind.speed.value)} ${w.wind.speed.unit}`,
        },
        {
            icon: 'M17 3H21V5H17V3M17 7H21V9H17V7M17 11H21V13H17.75L17 12.1V11M21 15V17H19C19 16.31 18.9 15.63 18.71 15H21M7 3V5H3V3H7M7 7V9H3V7H7M7 11V12.1L6.25 13H3V11H7M3 15H5.29C5.1 15.63 5 16.31 5 17H3V15M15 13V5C15 3.34 13.66 2 12 2S9 3.34 9 5V13C6.79 14.66 6.34 17.79 8 20S12.79 22.66 15 21 17.66 16.21 16 14C15.72 13.62 15.38 13.28 15 13M12 4C12.55 4 13 4.45 13 5V8H11V5C11 4.45 11.45 4 12 4Z',
            label: 'Point de rosée',
            value: `${Math.round(w.dewPoint.value)} ${w.dewPoint.unit}`,
        },
        {
            icon: 'M6,14.03A1,1 0 0,1 7,15.03C7,15.58 6.55,16.03 6,16.03C3.24,16.03 1,13.79 1,11.03C1,8.27 3.24,6.03 6,6.03C7,3.68 9.3,2.03 12,2.03C15.43,2.03 18.24,4.69 18.5,8.06L19,8.03A4,4 0 0,1 23,12.03C23,14.23 21.21,16.03 19,16.03H18C17.45,16.03 17,15.58 17,15.03C17,14.47 17.45,14.03 18,14.03H19A2,2 0 0,0 21,12.03A2,2 0 0,0 19,10.03H17V9.03C17,6.27 14.76,4.03 12,4.03C9.5,4.03 7.45,5.84 7.06,8.21C6.73,8.09 6.37,8.03 6,8.03A3,3 0 0,0 3,11.03A3,3 0 0,0 6,14.03M12,14.15C12.18,14.39 12.37,14.66 12.56,14.94C13,15.56 14,17.03 14,18C14,19.11 13.1,20 12,20A2,2 0 0,1 10,18C10,17.03 11,15.56 11.44,14.94C11.63,14.66 11.82,14.4 12,14.15M12,11.03L11.5,11.59C11.5,11.59 10.65,12.55 9.79,13.81C8.93,15.06 8,16.56 8,18A4,4 0 0,0 12,22A4,4 0 0,0 16,18C16,16.56 15.07,15.06 14.21,13.81C13.35,12.55 12.5,11.59 12.5,11.59',
            label: 'Précip. 24 h',
            value: `${w.precipitationSummary.past24Hours.value} ${w.precipitationSummary.past24Hours.unit}`,
        },
        {
            icon: 'M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4A8,8 0 0,1 20,12C20,14.4 19,16.5 17.3,18C15.9,16.7 14,16 12,16C10,16 8.2,16.7 6.7,18C5,16.5 4,14.4 4,12A8,8 0 0,1 12,4M14,5.89C13.62,5.9 13.26,6.15 13.1,6.54L11.81,9.77L11.71,10C11,10.13 10.41,10.6 10.14,11.26C9.73,12.29 10.23,13.45 11.26,13.86C12.29,14.27 13.45,13.77 13.86,12.74C14.12,12.08 14,11.32 13.57,10.76L13.67,10.5L14.96,7.29L14.97,7.26C15.17,6.75 14.92,6.17 14.41,5.96C14.28,5.91 14.15,5.89 14,5.89M10,6A1,1 0 0,0 9,7A1,1 0 0,0 10,8A1,1 0 0,0 11,7A1,1 0 0,0 10,6M7,9A1,1 0 0,0 6,10A1,1 0 0,0 7,11A1,1 0 0,0 8,10A1,1 0 0,0 7,9M17,9A1,1 0 0,0 16,10A1,1 0 0,0 17,11A1,1 0 0,0 18,10A1,1 0 0,0 17,9Z',
            label: 'Pression',
            value: `${Math.round(w.pressure.value)} ${w.pressure.unit}`,
        },
    ]
})

async function loadWeather() {
    const site = storeU.siteSélectionné
    if (!site?.latitudeGoogleMaps) return
    isLoading.value = true
    try {
        siteNom.value = site.nom

        const config = {
            params: {
                latitude: site.latitudeGoogleMaps,
                longitude: site.longitudeGoogleMaps,
            },
        }
        const response = await apiOmni.get('api/azuremaps/weather/currentConditions', config)

        if (Array.isArray(response.data.results) && response.data.results.length > 0) {
            weather.value = response.data.results[0]
        }
    } catch (error) {
        console.error('Error:', error)
        notifyError(error)
    } finally {
        isLoading.value = false
    }
}

watch(() => storeU.siteSélectionné, () => loadWeather())

onMounted(async () => {
    while (isPending.value || !isAuthenticated.value) {
        await new Promise((resolve) => setTimeout(resolve, 100))
    }
    await loadWeather()
})
</script>

<template>
    <v-card class="weather-card" :class="{ 'weather-card--hero': props.hero }"
        :rounded="props.hero ? 0 : 'xl'" :elevation="props.hero ? 0 : 3" :loading="isLoading"
        :style="{ background: props.hero ? 'transparent' : heroGradient }">
        <!-- Hero (Apple Weather style: centered) -->
        <div class="weather-hero text-center">
            <div class="location">{{ siteNom || '—' }}</div>
            <div class="hero-temp">
                {{ weather ? Math.round(weather.temperature.value) : '—' }}<span class="hero-deg">°</span>
            </div>
            <div class="condition">
                <v-icon :icon="heroIcon" size="20" class="mr-1" />
                {{ weather ? weather.phrase : 'Chargement…' }}
            </div>
            <div v-if="hiLo" class="hilo">
                Max. : {{ hiLo.hi }}°&nbsp;&nbsp;Min. : {{ hiLo.lo }}°
            </div>
            <div class="date-line text-capitalize">{{ currentDate }}</div>
        </div>

        <!-- Stats grid (frosted glass tiles) -->
        <div class="stats-wrap pa-4 pt-0">
            <div v-if="isLoading && !weather" class="d-flex flex-column" style="gap: 12px;">
                <v-skeleton-loader v-for="n in 3" :key="n" type="list-item-avatar" class="bg-transparent" />
            </div>
            <v-row v-else dense justify="center">
                <v-col v-for="stat in stats" :key="stat.label" cols="6" :sm="props.hero ? 4 : 6"
                    :md="props.hero ? 2 : 6">
                    <div class="stat-tile">
                        <v-icon :icon="stat.icon" size="20" class="stat-icon" />
                        <div class="stat-tile-body">
                            <div class="stat-label">{{ stat.label }}</div>
                            <div class="stat-value">{{ stat.value }}</div>
                        </div>
                    </div>
                </v-col>
            </v-row>
        </div>
    </v-card>
</template>

<style scoped>
.weather-card {
    overflow: hidden;
    color: #fff;
}

.weather-card--hero {
    color: #fff;
}

.weather-card--hero .stats-wrap {
    max-width: 1180px;
    margin: 0 auto;
}

.weather-hero {
    padding: 28px 20px 18px;
    color: #fff;
}

.location {
    font-size: 1.5rem;
    font-weight: 500;
    letter-spacing: 0.2px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    line-height: 1.2;
}

.hero-temp {
    font-size: 5.5rem;
    font-weight: 200;
    line-height: 1.05;
    letter-spacing: -2px;
}

.hero-deg {
    font-weight: 200;
}

.condition {
    font-size: 1rem;
    font-weight: 500;
    opacity: 0.95;
    display: inline-flex;
    align-items: center;
}

.hilo {
    font-size: 0.95rem;
    font-weight: 500;
    opacity: 0.9;
    margin-top: 2px;
}

.date-line {
    font-size: 0.8rem;
    opacity: 0.75;
    margin-top: 6px;
}

.stat-tile {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.12);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.12);
    transition: background 0.15s ease;
}

.stat-tile:hover {
    background: rgba(255, 255, 255, 0.2);
}

.stat-icon {
    color: rgba(255, 255, 255, 0.85);
}

.stat-tile-body {
    min-width: 0;
    line-height: 1.25;
}

.stat-label {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    opacity: 0.75;
}

.stat-value {
    font-size: 0.95rem;
    font-weight: 600;
}
</style>
